import { StorageSchemaTooNewError } from '@anytracker/core'

import { Button } from '../components/Button.js'
import styles from './AppShell.module.css'

/**
 * Shown when the database cannot be opened at all.
 *
 * This is outside the normal error boundary because it happens before there is
 * an app to put a boundary around. A blank page is the default outcome of a
 * failed `openDatabase()`, and a blank page is indistinguishable from "my data
 * is gone" — which for a schema mismatch is precisely the wrong conclusion, and
 * the one a user will reach in about two seconds.
 */
export function StartupError({ error }: { readonly error: unknown }) {
  const tooNew = error instanceof StorageSchemaTooNewError

  return (
    <div className={styles['notice']} role="alert">
      <h1>{tooNew ? 'This app needs updating' : 'Could not open your data'}</h1>

      {tooNew ? (
        <p>
          Your data was saved by a newer version of anytracker than the one loaded here —
          usually a cached copy of an older build. Reloading should fetch the current
          version. <strong>Nothing has been changed or deleted.</strong>
        </p>
      ) : (
        <p>
          The browser refused to open the database. This is most often private browsing,
          which blocks storage entirely, or a browser setting that clears site data.
        </p>
      )}

      <pre className={styles['noticeDetail']}>
        {error instanceof Error ? error.message : String(error)}
      </pre>

      <Button variant="primary" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  )
}
